import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, Loader } from "@/components/ui-helpers";
import { CreditCard, TrendingUp, Percent, ShoppingCart, Download } from "lucide-react";

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const PRESETS = {
  this_month: () => { const t = new Date(); return [iso(new Date(t.getFullYear(), t.getMonth(), 1)), iso(t)]; },
  last_month: () => { const t = new Date(); return [iso(new Date(t.getFullYear(), t.getMonth() - 1, 1)), iso(new Date(t.getFullYear(), t.getMonth(), 0))]; },
  last_90: () => { const t = new Date(); const s = new Date(t); s.setDate(t.getDate() - 89); return [iso(s), iso(t)]; },
  this_year: () => { const t = new Date(); return [iso(new Date(t.getFullYear(), 0, 1)), iso(t)]; },
};
const LABELS = [["this_month", "This month"], ["last_month", "Last month"], ["last_90", "Last 90 days"], ["this_year", "This year"], ["custom", "Custom"]];
const money = (n, cur = "CAD") => `${cur === "CAD" ? "CA" : ""}$${Number(n || 0).toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const when = (s) => (s ? new Date(s).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "");
const choiceLabel = { full: "Full balance", deposit: "Deposit", custom: "Part-payment" };

const csv = (name, header, rows) => {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const blob = new Blob([[header, ...rows].map((r) => r.map(esc).join(",")).join("\n")], { type: "text/csv" });
  const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = name; a.click(); URL.revokeObjectURL(a.href);
};

// Admin report: online payments collected per provider, processing fees earned, abandoned checkouts.
export function OnlinePaymentsReport() {
  const [preset, setPreset] = useState("this_month");
  const [[frm, to], setRange] = useState(PRESETS.this_month());
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!frm || !to || frm > to) return;
    setData(null);
    api.get("/reports/online-payments", { params: { frm, to } }).then((r) => setData(r.data)).catch(() => setData({ providers: [], totals: {}, recent_paid: [], abandoned: [] }));
  }, [frm, to]);
  const pick = (k) => { setPreset(k); if (k !== "custom") setRange(PRESETS[k]()); };
  const cur = data?.currency || "CAD";
  const t = data?.totals || {};

  const exportCsv = () => csv(`online-payments_${frm}_to_${to}.csv`, ["Status", "Date", "Client", "Provider", "Type", "Amount", "Fee"],
    [...data.recent_paid.map((r) => ["Paid", r.paid_at || r.created_at, r.client_name, r.provider, choiceLabel[r.choice] || "", r.amount, r.fee]),
     ...data.abandoned.map((r) => ["Abandoned", r.created_at, r.client_name, r.provider, choiceLabel[r.choice] || "", r.amount, r.fee])]);

  const Kpi = ({ icon: Icon, label, value, sub, color, testid }) => (
    <div className="rounded-xl border border-slate-200 p-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-xs font-semibold text-slate-500"><Icon className={`h-4 w-4 ${color}`} /> {label}</div>
      <div className="font-head text-2xl font-extrabold text-slate-900 mt-1">{value}</div>
      {sub && <div className="text-xs text-slate-400">{sub}</div>}
    </div>
  );

  const Row = ({ r }) => (
    <div className="py-2 flex items-center gap-3 text-sm">
      <Link to={r.client_id ? `/admin/clients/${r.client_id}?tab=billing` : "#"} className="font-medium text-slate-800 hover:text-primary truncate flex-1">{r.client_name}</Link>
      <span className="text-xs text-slate-400 hidden sm:inline">{r.provider === "stripe" ? "Card" : "PayPal"} · {choiceLabel[r.choice] || "Full balance"}{r.mode && r.mode !== "live" ? " · test" : ""}</span>
      <span className="text-xs text-slate-400 w-28 text-right">{when(r.paid_at || r.created_at)}</span>
      <b className="w-24 text-right">{money(r.amount, cur)}</b>
    </div>
  );

  return (
    <Card className="p-6 mt-6" data-testid="online-payments-report">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2"><CreditCard className="h-4 w-4 text-primary" /><h3 className="font-head font-bold text-slate-900">Online payments</h3></div>
        <button data-testid="online-report-download" onClick={exportCsv} disabled={!data || (!data.recent_paid?.length && !data.abandoned?.length)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-slate-200 text-xs font-semibold text-slate-600 hover:border-slate-300 disabled:opacity-40"><Download className="h-3.5 w-3.5" /> Download CSV</button>
      </div>
      <div className="flex flex-wrap items-center gap-2 mb-5">
        {LABELS.map(([k, l]) => <button key={k} data-testid={`online-report-range-${k}`} onClick={() => pick(k)} className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${preset === k ? "bg-primary text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>{l}</button>)}
        {preset === "custom" && <>
          <input type="date" value={frm} onChange={(e) => setRange([e.target.value, to])} className="px-2 py-1 rounded-lg border border-slate-200 text-xs" />
          <span className="text-xs text-slate-400">to</span>
          <input type="date" value={to} onChange={(e) => setRange([frm, e.target.value])} className="px-2 py-1 rounded-lg border border-slate-200 text-xs" />
        </>}
      </div>
      {!data ? <Loader /> : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Kpi testid="online-kpi-collected" icon={TrendingUp} color="text-emerald-600" label="Collected online" value={money(t.collected, cur)} sub={`${t.paid_count || 0} payments · ${t.partial_count || 0} part-payments`} />
            <Kpi testid="online-kpi-fees" icon={Percent} color="text-indigo-600" label="Processing fees earned" value={money(t.fees, cur)} sub={`Charged in total ${money(t.charged, cur)}`} />
            <Kpi testid="online-kpi-abandoned" icon={ShoppingCart} color="text-amber-600" label="Abandoned checkouts" value={t.abandoned_count || 0} sub={`${money(t.abandoned_value, cur)} not completed`} />
            <Kpi testid="online-kpi-conversion" icon={CreditCard} color="text-primary" label="Checkout completion" value={`${t.conversion || 0}%`} sub={`${t.started || 0} checkouts started`} />
          </div>
          {data.providers.length > 0 && (
            <div className="mt-5 overflow-x-auto">
              <table className="w-full text-sm" data-testid="online-report-providers">
                <thead className="text-xs text-slate-500 border-b border-slate-200"><tr><th className="text-left py-2">Provider</th><th className="text-right">Payments</th><th className="text-right">Collected</th><th className="text-right">Fees earned</th><th className="text-right">Abandoned</th><th className="text-right">Completion</th></tr></thead>
                <tbody className="divide-y divide-slate-100">
                  {data.providers.map((p) => (
                    <tr key={p.provider} data-testid={`online-report-provider-${p.provider}`}>
                      <td className="py-2 font-semibold text-slate-800">{p.label}</td><td className="text-right">{p.paid_count}</td><td className="text-right">{money(p.collected, cur)}</td>
                      <td className="text-right">{money(p.fees, cur)}</td><td className="text-right">{p.abandoned_count} · {money(p.abandoned_value, cur)}</td><td className="text-right">{p.conversion}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="grid lg:grid-cols-2 gap-5 mt-5">
            <div>
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Recent online payments</div>
              {data.recent_paid.length ? <div className="divide-y divide-slate-100" data-testid="online-report-paid">{data.recent_paid.map((r) => <Row key={r.id} r={r} />)}</div> : <div className="text-sm text-slate-400 py-4">No online payments in this period.</div>}
            </div>
            <div>
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Abandoned checkouts <span className="normal-case font-normal text-slate-400">— started but not paid (worth a follow-up)</span></div>
              {data.abandoned.length ? <div className="divide-y divide-slate-100" data-testid="online-report-abandoned">{data.abandoned.map((r) => <Row key={r.id} r={r} />)}</div> : <div className="text-sm text-slate-400 py-4">No abandoned checkouts.</div>}
            </div>
          </div>
        </>
      )}
    </Card>
  );
}
